import { User, UserSchema } from '@campai/lib-common/entities'

import { client, TRPCClientError } from '@/utils/trpc'
import { KeyValueStorage } from '@/utils'

import { LoadCreateUserFunc } from './types'
import { getUtmData } from './utmData'

const tryLoadUser = async (
    forceLoad: boolean,
    update: (user: User | null) => void | Promise<void>,
    storage: KeyValueStorage,
    tries: number = 0
): Promise<void> => {
    try {
        if (!forceLoad) {
            // Try to read from cache first and if has then only refresh and immediately return here
            const user = await storage.get<User>('cache.user')
            if (user && UserSchema.safeParse(user).success && user.timezone) {
                try {
                    // Immediately update
                    await update(user)

                    // Get fresh data in the background
                    client.currentUser.getUser
                        .query()
                        .then((user: Partial<User>) => {
                            // Store updated user if any
                            storage.set('cache.user', user)

                            // Call update
                            return update(user as User)
                        })
                        .catch((error: any) => {
                            throw error
                        })

                    // we're done here
                    return
                } catch {
                    // -- ignore and load regularily
                }
            }
        }

        // Await initial user
        const user = (await client.currentUser.getUser.query()) as User

        // Store updated user if any
        storage.set('cache.user', user)

        // Make initial update
        await update(user)
    } catch (error) {
        // retry on dev env until the server has started
        if (
            import.meta.env.DEV &&
            error instanceof TRPCClientError &&
            error.message === 'Failed to fetch' &&
            tries < 30
        ) {
            await new Promise((resolve) => setTimeout(resolve, 1000))

            return tryLoadUser(true, update, storage, tries + 1)
        }

        throw error
    }
}

export const loadCreateUser: LoadCreateUserFunc = async (forceLoad, update, storage) => {
    try {
        await tryLoadUser(forceLoad, update, storage)
    } catch (error) {
        if (error instanceof TRPCClientError) {
            if (error.data?.code === 'NOT_FOUND') {
                const utmData = getUtmData()

                await client.currentUser.createUser.mutate({ utm: utmData || undefined })

                return tryLoadUser(true, update, storage)
            }
        }

        throw error
    }
}

export const loadCreateUserNull: LoadCreateUserFunc = async (forceLoad, update) => update(null)
